import React from 'react';
import { getReportGuidelineRefs } from '../services/accuracyGuidelineLabels';
import {
  getGuidelineDisplayName,
  getJobDisplayName,
  type AccuracyScopeMetadata,
} from '../services/accuracyScopeMetadata';
import { URL_STATUS_LABELS } from '../services/accuracyEvaluationScope';
import type { SavedAccuracyReport } from '../types/accuracy';

function jobLabel(jobId: string, metadata?: AccuracyScopeMetadata | null): string {
  return getJobDisplayName(jobId, metadata);
}

/** Labels for filters chosen when the report was created. */
export function getReportScopeLabels(
  report: SavedAccuracyReport,
  metadata?: AccuracyScopeMetadata | null
) {
  const f = report.filters;
  const guidelineRefs = getReportGuidelineRefs(report, metadata);
  const allJobCount = metadata?.jobsById.size ?? 0;
  const jobs =
    f.job_ids.length === 0 || (allJobCount > 0 && f.job_ids.length >= allJobCount)
      ? 'すべてのジョブ'
      : f.job_ids.map((id) => jobLabel(id, metadata)).join('、');
  const urlStatuses =
    f.url_statuses.length > 0
      ? f.url_statuses.map((s) => URL_STATUS_LABELS[s]).join('、')
      : '—';

  return {
    guidelineRefs,
    jobs,
    urlStatuses,
    urlCount: report.result?.url_count,
  };
}

export function AccuracyReportScopeCompact({
  report,
  metadata,
}: {
  report: SavedAccuracyReport;
  metadata?: AccuracyScopeMetadata | null;
}) {
  const scope = getReportScopeLabels(report, metadata);
  const multipleGuidelines = scope.guidelineRefs.length > 1;

  return (
    <dl className="accuracy-report-scope-compact" aria-label="レポート作成時の評価条件">
      {multipleGuidelines ? (
        <div className="accuracy-report-scope-guidelines-block">
          <dt>ガイドライン</dt>
          <dd className="accuracy-report-scope-guidelines-list">
            {scope.guidelineRefs.map((ref) => (
              <span key={`${ref.guideline_id}:${ref.version}`} className="accuracy-guideline-chip">
                <span className="accuracy-guideline-chip-name">
                  {getGuidelineDisplayName(ref.guideline_id, metadata)}
                </span>
                <span className="accuracy-guideline-chip-version">v{ref.version}</span>
              </span>
            ))}
          </dd>
        </div>
      ) : (
        <>
          <div>
            <dt>ガイドライン</dt>
            <dd>{getGuidelineDisplayName(scope.guidelineRefs[0]?.guideline_id ?? '', metadata)}</dd>
          </div>
          <div>
            <dt>バージョン</dt>
            <dd>v{scope.guidelineRefs[0]?.version ?? '—'}</dd>
          </div>
        </>
      )}
      <div>
        <dt>ジョブ</dt>
        <dd>{scope.jobs}</dd>
      </div>
      <div>
        <dt>URLステータス</dt>
        <dd>{scope.urlStatuses}</dd>
      </div>
    </dl>
  );
}

export function AccuracyReportFiltersSummary({
  report,
  metadata,
}: {
  report: SavedAccuracyReport;
  metadata?: AccuracyScopeMetadata | null;
}) {
  const scope = getReportScopeLabels(report, metadata);
  const multipleGuidelines = scope.guidelineRefs.length > 1;

  return (
    <dl className="accuracy-report-filters-summary">
      {multipleGuidelines ? (
        <div className="accuracy-report-filters-summary-guidelines">
          <dt>ガイドライン</dt>
          <dd>
            <ul className="accuracy-report-guideline-ref-list">
              {scope.guidelineRefs.map((ref) => (
                <li key={`${ref.guideline_id}:${ref.version}`}>
                  {getGuidelineDisplayName(ref.guideline_id, metadata)}
                  <span className="accuracy-guideline-version-tag">v{ref.version}</span>
                </li>
              ))}
            </ul>
          </dd>
        </div>
      ) : (
        <>
          <div>
            <dt>ガイドライン</dt>
            <dd>{getGuidelineDisplayName(scope.guidelineRefs[0]?.guideline_id ?? '', metadata)}</dd>
          </div>
          <div>
            <dt>バージョン</dt>
            <dd>v{scope.guidelineRefs[0]?.version ?? '—'}</dd>
          </div>
        </>
      )}
      <div>
        <dt>ジョブ</dt>
        <dd>{scope.jobs}</dd>
      </div>
      <div>
        <dt>URLステータス</dt>
        <dd>{scope.urlStatuses}</dd>
      </div>
      <div>
        <dt>評価 URL 数</dt>
        <dd>{scope.urlCount != null ? `${scope.urlCount} 件` : '—'}</dd>
      </div>
    </dl>
  );
}
